(function(angular, _) {
    'use strict';

    angular.module('wtApp.dataService')
        .factory('WtComparisonViewMode', ['$injector', function($injector) {
            const $q = $injector.get('$q');
            const $log = $injector.get('$log');
            const Filters = $injector.get('Filters');
            const dateService = $injector.get('dateService');
            const WtObservable = $injector.get('WtObservable');
            const segmentService = $injector.get('segmentService');
            const viewModeComparison = $injector.get('viewModeComparison');

            /**
             * @name WtComparisonViewMode
             * @description Factory responsible for the analysis comparison view mode management
             */
            return class WtComparisonViewMode {

                /**
                 * @ngdoc property
                 * @name WtComparisonViewMode.validate
                 * @description A map of actions that can be emitted by the viewModes "changes" observable
                 * @returns {Object}
                 * @static
                 */
                static get ACTIONS() {
                    return {
                        /**
                         * Generic event informing about any type of change performed on the comparison view mode configuration
                         */
                        VIEW_MODES_COMPARISON_UPDATE: 'VIEW_MODES_COMPARISON_UPDATE',

                        /**
                         * Triggered when the type property of the comparison view mode configuration is being changed
                         */
                        VIEW_MODES_COMPARISON_TYPE_UPDATE: 'VIEW_MODES_COMPARISON_TYPE_UPDATE',

                        /**
                         * Triggered when the values of the comparison type are being changed
                         */
                        VIEW_MODES_COMPARISON_VALUES_UPDATE: 'VIEW_MODES_COMPARISON_VALUES_UPDATE',

                        /**
                         * Triggered when the sorting of the comparison type is being changed
                         */
                        VIEW_MODES_COMPARISON_SORTING_UPDATE: 'VIEW_MODES_COMPARISON_SORTING_UPDATE',

                        /**
                         * Triggered when the collapsed map of the comparison type is being changed
                         */
                        VIEW_MODES_COMPARISON_COLLAPSED_UPDATE: 'VIEW_MODES_COMPARISON_COLLAPSED_UPDATE'
                    };
                }

                /**
                 * @ngdoc property
                 * @name WtComparisonViewMode#TYPES
                 * @description Proxies the types of the view mode comparison service
                 * @returns {Object}
                 */
                get TYPES() {
                    return viewModeComparison.TYPES;
                }

                /**
                 * @ngdoc property
                 * @name WtComparisonViewMode#pristineConfig
                 * @description Exposes the pristine viewMode configuration object to the outer world
                 * @returns {Object}
                 */
                get pristineConfig() {
                    return this.pristineData_;
                }

                /**
                 * @ngdoc property
                 * @name WtComparisonViewMode#config
                 * @description Exposes the viewMode configuration object to the outer world
                 * @returns {Object}
                 */
                get config() {
                    return this.data_;
                }

                /**
                 * @ngdoc property
                 * @name WtComparisonViewMode#actions
                 * @description Exposes the possible actions map to be used by the columns changes consumers
                 * @returns {Object}
                 */
                get actions() {
                    return WtComparisonViewMode.ACTIONS;
                }

                /**
                 * @ngdoc property
                 * @name WtComparisonViewMode#isActiveComparisonApplicable
                 * @description Determines if the currently active comparison type configuration is applicable in terms of query building
                 * @returns {boolean}
                 */
                get isActiveComparisonApplicable() {
                    return viewModeComparison.canApplyComparisonByType(this.data_);
                }

                /**
                 * @ngdoc property
                 * @name WtComparisonViewMode#activeReplicationFactor
                 * @description Returns the comparison values of the currently active comparison type
                 * @returns {number}
                 */
                get activeReplicationFactor() {
                    return viewModeComparison.getReplicationFactor(this.data_);
                }

                /**
                 * @ngdoc property
                 * @name WtComparisonViewMode#activeComparisonValues
                 * @description Returns the comparison values of the currently active comparison type
                 * @returns {Array<Object>}
                 */
                get activeComparisonValues() {
                    return viewModeComparison.getValidComparisonObjects(this.data_);
                }

                /**
                 * @ngdoc property
                 * @name WtComparisonViewMode#activeSortingObjects
                 * @description Returns the list of sorting objects for the currently selected comparison type
                 * @returns {Array<Object>}
                 */
                get activeSortingObjects() {
                    return viewModeComparison.getSortingObjects(this.data_);
                }

                /**
                 * @ngdoc property
                 * @name WtComparisonViewMode#timeComparisonValues
                 * @description Returns the comparison values of the time comparison type
                 * @returns {Array<Object>}
                 */
                get timeComparisonValues() {
                    return viewModeComparison.getValidComparisonObjects(this.data_, this.TYPES.TIME);
                }

                /**
                 * @ngdoc property
                 * @name WtComparisonViewMode#segmentComparisonValues
                 * @description Returns the comparison values of the segment comparison type
                 * @returns {Array<Object>}
                 */
                get segmentComparisonValues() {
                    return viewModeComparison.getValidComparisonObjects(this.data_, this.TYPES.SEGMENT);
                }

                /**
                 * @ngdoc method
                 * @name WtComparisonViewMode#constructor
                 * @description WtComparisonViewMode constructor
                 * @param {Object} config The viewMode configuration
                 * @throws {Error} Invalid argument exception
                 */
                constructor(config) {
                    /**
                     * Holds the original data
                     * @type {Object}
                     * @private
                     */
                    this.pristineData_ = Object.freeze(_.cloneDeep(config));

                    /**
                     * Holds the current data
                     * @type {Object}
                     * @private
                     */
                    this.data_ = _.cloneDeep(config);

                    /**
                     * A map of callbacks to invoke whenever the viewMode configuration is changed
                     * IMPORTANT: Deregister the callbacks before destroying the observing object to allow it to be garbage collected
                     * TODO Should be replaced in near future by the Observer pattern
                     * @type {Map}
                     * @private
                     */
                    this.onUpdate_ = new Map();

                    /**
                     * The observer object that can be used to push changes into the observable
                     * @example
                     *  - Notify the listeners about a new value: this.changesEmitter_.next('value');
                     *  - Notify the listeners about an error: this.changesEmitter_.error(new Error('Something went wrong'))
                     *  - Notify the listeners about the completion: this.changesEmitter_.complete()
                     * @type {null|Object}
                     * @private
                     */
                    this.changesEmitter_ = null;

                    /**
                     * The observable object that can be subscribed to listen to the changes on the viewModes instance
                     * @example
                     *  const subscription = viewModes.changes.subscribe((action) => {
                     *      const type = action.type;
                     *      const payload = action.payload;
                     *      // React to the viewModes change
                     *  });
                     *
                     *  // Cleanup method should be invoked when the listener is destroyed or does not need the updates
                     *  subscription.unsubscribe();
                     * @type {WtObservable}
                     */
                    this.changes = new WtObservable((observer) => {
                        this.changesEmitter_ = observer;
                    });

                    this.initCollapsibleColumns_();
                }

                /**
                 * @ngdoc method
                 * @name WtComparisonViewMode#isTimeBasedComparison
                 * @description Determines if a given or the current comparison type is that of time
                 * @param   {string=}  type_
                 * @returns {boolean}
                 */
                isTimeBasedComparison(type_ = null) {
                    const type = type_ || this.get('type');
                    return type === this.TYPES.TIME;
                }

                /**
                 * @ngdoc method
                 * @name WtComparisonViewMode#isSegmentBasedComparison
                 * @description Determines if a given or the current comparison type is that of segment
                 * @param   {string=}  type_
                 * @returns {boolean}
                 */
                isSegmentBasedComparison(type_ = null) {
                    const type = type_ || this.get('type');
                    return type === this.TYPES.SEGMENT;
                }

                /**
                 * @ngdoc method
                 * @name WtComparisonViewMode#setComparisonType
                 * @description Plain setter
                 * @returns {WtComparisonViewMode}
                 */
                setComparisonType(type) {
                    return this.set('type', type);
                }

                /**
                 * @ngdoc method
                 * @name WtComparisonViewMode#resolveTimeComparisonValues
                 * @description Either retrieves the 'valid' values of a time comparison or returns the 'proposed' valid values
                 * @param   {Object} timeFilter_
                 * @returns {Promise}
                 */
                resolveTimeComparisonValues(timeFilter_) {
                    if (viewModeComparison.canApplyComparisonByType(this.data_, this.TYPES.TIME)) {
                        return new $q(resolve => resolve(this.timeComparisonValues));
                    }

                    return this.initTimeComparisonValues_(timeFilter_);
                }

                /**
                 * @ngdoc method
                 * @name WtComparisonViewMode#initTimeComparisonValues_
                 * @description Inits the values of time comparison
                 * @param   {Object} timeFilter_
                 * @returns {Promise}
                 * @private
                 */
                initTimeComparisonValues_(timeFilter_) {
                    const timeFilter = _.last(this.timeComparisonValues) || _.cloneDeep(timeFilter_);
                    return new $q((resolve, reject) => {
                        dateService
                            .resolveTimeRangeByFilter(timeFilter)
                            .then(({filterOffsettedWeekday}) => {
                                const timeFilters = [timeFilter, filterOffsettedWeekday];
                                this.set(`values.${this.TYPES.TIME}`, timeFilters);
                                return resolve(timeFilters);
                            })
                            .catch(e => {
                                $log.error(e);
                                return reject(e);
                            });
                    });
                }

                getColumnCollapseConfiguration() {
                    return this.get('collapsed');
                }

                setColumnCollapseConfiguration(columnAlias, values) {
                    this.set(`collapsed.${columnAlias}`, values);
                }

                /**
                 * @ngdoc method
                 * @name WtComparisonViewMode#setComparisonTimeFilters
                 * @description Plain setter
                 * @param   {Array<Object>} timeFilters List of time filters
                 * @returns {WtComparisonViewMode}
                 */
                setComparisonTimeFilters(timeFilters) {
                    return this.set(`values.${this.TYPES.TIME}`, timeFilters);
                }

                /**
                 * @ngdoc method
                 * @name WtComparisonViewMode#resolveSegmentComparisonValues
                 * @description Either retrieves the 'valid' values of a segment comparison or returns the 'proposed' valid values
                 * @returns {Promise}
                 */
                resolveSegmentComparisonValues() {
                    if (viewModeComparison.canApplyComparisonByType(this.data_, this.TYPES.SEGMENT)) {
                        return new $q(resolve => resolve(this.segmentComparisonValues));
                    }

                    return this.initSegmentComparisonValues_();
                }

                /**
                 * @ngdoc method
                 * @name WtComparisonViewMode#initSegmentComparisonValues_
                 * @description Inits the values of segment comparison
                 * @returns {Promise}
                 * @private
                 */
                initSegmentComparisonValues_() {
                    return new $q((resolve, reject) => {
                        try {
                            const selectedSegmentIds = _.map(this.segmentComparisonValues, 'segmentID');
                            const additionalSegmentConnectors = _.chain(segmentService.getActiveSegments())
                                                                    .map()
                                                                    .reject(segment => _.includes(selectedSegmentIds, segment.id))
                                                                    .take(2 - selectedSegmentIds.length)
                                                                    .map(segment => segmentService.getSegmentAsConnector(segment))
                                                                    .value();
                            const segmentConnectors = [...this.segmentComparisonValues, ...additionalSegmentConnectors];
                            this.set(`values.${this.TYPES.SEGMENT}`, segmentConnectors);
                            return resolve(segmentConnectors);
                        } catch (e) {
                            $log.error(e);
                            return reject(e);
                        }
                    });
                }

                /**
                 * @ngdoc method
                 * @name WtComparisonViewMode#setComparisonSegments
                 * @description Plain setter
                 * @param   {Array<Object>} segmentConnectors List of segment connectors
                 * @returns {WtComparisonViewMode}
                 */
                setComparisonSegments(segmentConnectors) {
                    return this.set(`values.${this.TYPES.SEGMENT}`, segmentConnectors);
                }

                initComparisonValues(timeFilter = null) {
                    return this.isTimeBasedComparison() ? this.initTimeComparisonValues_(timeFilter) : this.initSegmentComparisonValues_();
                }

                /**
                 * @ngdoc method
                 * @name WtComparisonViewMode#getComparisonValueString
                 * @description Retrieves a string representation for a given comparison value/object
                 * @param   {Object} comparisonValue A comparison object - i.e. time filter or segment connector
                 * @param   {string=} type_
                 * @returns {string}
                 */
                getComparisonValueString(comparisonValue, type_ = null) {
                    const type = type_ || this.get('type');

                    if (this.isTimeBasedComparison(type)) {
                        return Filters.getTimeRange(comparisonValue);
                    }

                    return segmentService.getTitleFromSegmentConnector(comparisonValue);
                }

                /**
                 * @ngdoc method
                 * @name WtComparisonViewMode#getActiveComparisonValuesByIndex
                 * @description Retrieves a list of comparison objects strings associated with a sub-column index
                 * @param   {number} index The index of a sub alias column
                 * @returns {Array<string>}
                 */
                getActiveComparisonValuesByIndex(index) {
                    const comparisonValues = this.activeComparisonValues;
                    if (index < comparisonValues.length) {
                        return [this.getComparisonValueString(_.get(comparisonValues, index))];
                    }

                    const step = comparisonValues.length - 1;

                    return _.chain(comparisonValues)
                            .at([0, index - step])
                            .map(comparisonValue => this.getComparisonValueString(comparisonValue))
                            .value();
                }

                /**
                 * @ngdoc method
                 * @name WtComparisonViewMode#setSortingObject
                 * @description Sets the sorting object - alias and direction - of comparison sub columns
                 * @param   {string} alias
                 * @param   {string} direction
                 * @param   {string=} type_
                 * @returns {WtComparisonViewMode}
                 */
                setSortingObject(alias, direction, type_ = null) {
                    const type = type_ || this.get('type');
                    return this.set(`sorting.${type}`, [{alias, direction}]);
                }

                /**
                 * @ngdoc method
                 * @name WtComparisonViewMode#emptySortingObject
                 * @description Empties the sorting object list
                 * @param   {string=} type_
                 * @returns {WtComparisonViewMode}
                 */
                emptySortingObject(type_ = null) {
                    const type = type_ || this.get('type');
                    return this.set(`sorting.${type}`, []);
                }

                initCollapsibleColumns_() {
                    const collapsed = this.get('collapsed');
                    if (_.isNil(collapsed)) {
                        this.set('collapsed', Object.create(null));
                    }
                }

                hasMultipleComparisons() {
                    return this.activeComparisonValues.length > 2;
                }

                /**
                 * @ngdoc method
                 * @name WtComparisonViewMode#toJSON
                 * @description Converts the instance into a serializable object
                 * @returns {Object}
                 */
                toJSON() {
                    return this.data_;
                }

                /**
                 * @ngdoc method
                 * @name WtComparisonViewMode#toString
                 * @description Stringify the instance
                 * @returns {string}
                 */
                toString() {
                    return JSON.stringify(
                        this.toJSON()
                    );
                }

                /**
                 * @ngdoc method
                 * @name WtComparisonViewMode#dispatchAction
                 * @description Emits an event in the viewModes changes observable
                 * @param {string} type The event name
                 * @param {Object=} payload The payload of the event
                 * @returns {WtComparisonViewMode}
                 */
                dispatchAction(type, payload = {}) {
                    if (type) {
                        this.changesEmitter_.next({type, payload});
                    }

                    return this;
                }

                /**
                 * @ngdoc method
                 * @name WtComparisonViewMode#isPristine
                 * @description Checks if the viewMode configuration was changed since it was initialized
                 * @returns {boolean}
                 */
                isPristine() {
                    return _.isEqual(this.pristineData_, this.data_);
                }

                /**
                 * @ngdoc method
                 * @name WtComparisonViewMode#get
                 * @description Analysis viewMode configuration accessor
                 * @param {string} iPath The path within the analysis viewMode configuration (see lodash _.get())
                 * @returns {*}
                 */
                get(iPath) {
                    const path = iPath ? String(iPath) : null;
                    return path ? _.get(this.data_, path) : null;
                }

                /**
                 * @ngdoc method
                 * @name WtComparisonViewMode#set
                 * @description Comparison viewMode configuration setter
                 * @param {string} path_ The path within the comparison viewMode (see lodash _.set())
                 * @param {*} currentValue The value to be set
                 * @returns {WtComparisonViewMode}
                 */
                set(path_, currentValue) {
                    const path = path_ ? String(path_) : null;
                    const previousValue = this.get(path);

                    if (!path || _.isEqual(previousValue, currentValue)) {
                        return this;
                    }

                    _.set(this.data_, path, currentValue);

                    const payload = {path, previousValue, currentValue};

                    const pathRoot = _.chain(path).split('.').head().value();

                    switch (pathRoot) {
                        case 'type':
                            return this.dispatchAction(this.actions.VIEW_MODES_COMPARISON_TYPE_UPDATE, payload);
                        case 'collapsed':
                            return this.dispatchAction(this.actions.VIEW_MODES_COMPARISON_COLLAPSED_UPDATE, payload);
                        case 'sorting':
                            return this.dispatchAction(this.actions.VIEW_MODES_COMPARISON_SORTING_UPDATE, payload);
                        case 'values':
                            return this.dispatchAction(this.actions.VIEW_MODES_COMPARISON_VALUES_UPDATE, payload);
                        default:
                            return this.dispatchAction(this.actions.VIEW_MODES_COMPARISON_UPDATE, payload);
                    }
                }

                /**
                 * @ngdoc method
                 * @name WtComparisonViewMode#destroy
                 * @description Method invoked before the instance is destroyed to release the resources and perform the cleanup.
                 */
                destroy() {
                    if (this.changesEmitter_) {
                        this.changesEmitter_.complete();
                    }
                }
            };
        }]);
})(window.angular, window._);
